import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';
const TOKEN_KEY = 'gardeno_admin_token';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};
const GREEN = '#34D399';
const AMBER = '#FBBF24';
const RED = '#F87171';
const BLUE = '#3B82F6';

interface Funnel {
  days: number;
  users_with_reminders: number;
  devices_authorized: number;
  devices_denied: number;
  devices_undetermined: number;
  devices_alerts_off: number;
  scheduled: number;
  delivered: number;
  opened: number;
  marked_done: number;
  snoozed: number;
  dismissed: number;
  ignored: number;
  reminders_active: number;
  done_on_time_rate: number;
  overdue_reminders: number;
}

// Care reminder health: can we reach people, do notifications land, and do
// they act on them. "Delivered" is estimated on-device — iOS gives no
// delivery receipt for local notifications — so it is labelled as such.
export default function ReminderFunnel({ token, isMobile }: { token: string; isMobile: boolean }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Funnel | null>(null);
  const [loading, setLoading] = useState(false);

  const on401 = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    window.location.reload();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/reminder-funnel?days=${days}`, {
        headers: authHeaders(token),
      });
      if (res.status === 401 || res.status === 403) { on401(); return; }
      if (res.ok) {
        const d = await res.json();
        setData((d.data ?? d) as Funnel);
      }
    } catch {
      /* silent — the panel shows an empty state */
    } finally {
      setLoading(false);
    }
  }, [token, days, on401]);

  useEffect(() => { load(); }, [load]);

  const reachableDevices = data ? data.devices_authorized : 0;
  const knownDevices = data
    ? data.devices_authorized + data.devices_denied + data.devices_undetermined
    : 0;
  const reachPct = knownDevices > 0 ? Math.round((reachableDevices / knownDevices) * 100) : 0;

  const actedOn = data
    ? data.opened + data.marked_done + data.snoozed + data.dismissed
    : 0;
  const actionPct = data && data.delivered > 0
    ? Math.round((actedOn / data.delivered) * 100)
    : 0;

  const card: React.CSSProperties = {
    background: C.panel,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: 16,
  };

  return (
    <div style={{ marginTop: 26 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, flexWrap: 'wrap', gap: 10,
      }}>
        <p style={{ margin: 0, color: C.sub, fontSize: 12, maxWidth: 520, lineHeight: 1.5 }}>
          Reminders are scheduled on the device as local iOS notifications, so they only fire
          for users who granted notification permission.
        </p>
        <div style={{ display: 'flex', gap: 6 }}>
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: `1px solid ${days === d ? C.accent : C.border}`,
                background: days === d ? C.accent : 'transparent',
                color: days === d ? '#fff' : C.sub,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && !data && (
        <div style={{ ...card, color: C.sub, fontSize: 13 }}>Loading…</div>
      )}

      {data && (
        <>
          {/* Reach — the ceiling on everything below */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: 10,
            marginBottom: 12,
          }}>
            <Stat label="Users with reminders" value={data.users_with_reminders} />
            <Stat label="Active reminders" value={data.reminders_active} />
            <Stat
              label="Devices that can receive"
              value={knownDevices > 0 ? `${reachableDevices}/${knownDevices}` : '—'}
              sub={knownDevices > 0 ? `${reachPct}% reachable` : 'no reports yet'}
              color={knownDevices > 0 && reachPct < 50 ? RED : GREEN}
            />
            <Stat
              label="Overdue reminders"
              value={data.overdue_reminders}
              color={data.overdue_reminders > 0 ? AMBER : GREEN}
            />
          </div>

          {/* Delivery + interaction */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 12,
          }}>
            <div style={card}>
              <SectionLabel>Delivery (last {data.days} days)</SectionLabel>
              <Row label="Scheduled" value={data.scheduled} color={BLUE} />
              <Row label="Delivered (estimated)" value={data.delivered} color={GREEN} />
              <p style={{ margin: '10px 0 0', color: C.sub, fontSize: 11, lineHeight: 1.5 }}>
                iOS does not confirm delivery of local notifications. Delivered counts the ones
                we scheduled whose time passed and which left the pending queue.
              </p>
            </div>

            <div style={card}>
              <SectionLabel>What users did</SectionLabel>
              <Row label="Marked done" value={data.marked_done} color={GREEN} />
              <Row label="Opened the app" value={data.opened} color={BLUE} />
              <Row label="Snoozed" value={data.snoozed} color={AMBER} />
              <Row label="Dismissed" value={data.dismissed} color={C.sub} />
              <Row label="Ignored" value={data.ignored} color={RED} />
              <div style={{
                marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`,
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span style={{ color: C.sub, fontSize: 12 }}>Acted on</span>
                <span style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>
                  {data.delivered > 0 ? `${actionPct}%` : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* The outcome that actually matters */}
          <div style={{ ...card, marginTop: 12 }}>
            <SectionLabel>Outcome</SectionLabel>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={{
                color: data.done_on_time_rate >= 0.5 ? GREEN : AMBER,
                fontSize: 28, fontWeight: 800,
              }}>
                {Math.round(data.done_on_time_rate * 100)}%
              </span>
              <span style={{ color: C.sub, fontSize: 12 }}>
                of active reminders have been completed at least once
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      color: C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: 0.5, marginBottom: 10,
    }}>
      {children}
    </div>
  );
}

function Row({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0',
    }}>
      <span style={{ color: C.sub, fontSize: 12 }}>{label}</span>
      <span style={{ color, fontSize: 14, fontWeight: 700 }}>{value}</span>
    </div>
  );
}

function Stat({ label, value, sub, color }: {
  label: string; value: number | string; sub?: string; color?: string;
}) {
  return (
    <div style={{
      background: C.panelAlt, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{ color: color ?? C.text, fontSize: 20, fontWeight: 800 }}>{value}</div>
      <div style={{ color: C.sub, fontSize: 11, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ color: C.sub, fontSize: 10, marginTop: 2, opacity: 0.8 }}>{sub}</div>}
    </div>
  );
}
